import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useStore } from '../../../hooks';

import TronixLoaderVideo from '../../../assets/media/loader/tronix-loader.mp4';
import TronixLoaderGif from '../../../assets/media/loader/tronix-loader.gif';
import PoweredByPG from '../../../assets/media/loader/powered-by-pascal.png';
import PGLoaderVideo from '../../../assets/media/loader/pg-loader-new.mp4';
import PGLoaderGif from '../../../assets/media/loader/pg-loader-new.gif';

import './style.scss';

interface DefaultLoaderProps {
  isTronix?: boolean;
  hasPascalLoader?: boolean;
}

export const DefaultLoader: React.FC<DefaultLoaderProps> = ({
  isTronix = false,
  hasPascalLoader = false
}) => {

  const { loaderStore } = useStore();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    if (hasPascalLoader) {
      autoplayVideo();
      trackVideoCurrentTime();
    }
  }, [hasPascalLoader]);

  const autoplayVideo = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      // Attempt to play the video
      await video.play();
      // If the play() promise resolves, it means autoplay is working, 
      // and therefore, Low Power Mode is likely OFF.
      video.addEventListener('ended', () => {
        video.currentTime = isTronix ? 2.9 : 0;
        video.play().catch(console.error);
      }, false);
    } catch (error) {
      // If the play() promise is rejected, it's a strong indicator 
      // that Low Power Mode is ON.
      loaderStore.setVideoCurrentTime(0);
      setVideoFailed(true);
    }
  }, []);

  const trackVideoCurrentTime = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    video.addEventListener('timeupdate', () => {
      loaderStore.setVideoCurrentTime(video.currentTime);
    });
  }, []);

  if (!hasPascalLoader) {
    return null;
  }

  return (
    <div className={`global-loader-wrapper ${isTronix ? 'tronix' : ''}`}>
      {
        videoFailed ? (
          <img
            alt="loader"
            className="loader-gif"
            src={isTronix ? TronixLoaderGif : PGLoaderGif}
          />
        ) : (
          <video
            muted
            playsInline
            ref={videoRef}
            className="loader-video"
          >
            <source
              type="video/mp4"
              src={isTronix ? TronixLoaderVideo : PGLoaderVideo}
            />
          </video>
        )
      }
      {
        isTronix && (
          <img
            className="powered-by-image"
            alt="Powered By Pascal Gaming"
            src={PoweredByPG}
          />
        )
      }
    </div>
  );
};
